import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { API_BASE, apiFetch, setOnUnauthorized } from "./apiClient";
import { loginWithPasskey } from "@/features/passkey";

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Client-only flags. The session itself lives in an HttpOnly cookie that JS
// can't read (TNK-54), so SIGNED_IN_HINT just lets a cold start render the
// right screen before /api/auth/me round-trips. GUEST_FLAG drives the
// dev-only guest bypass and is compiled out of production builds — every read
// of it is behind `import.meta.env.DEV`, a static false in `vite build`.
const SIGNED_IN_HINT = "tcg-web.signed-in";
const GUEST_FLAG = "tcg-web.guest";
// Last-known role, cached so an offline cold start keeps the right UI instead
// of collapsing to the most-restricted view (TNK-81). Refreshed from /auth/me.
const ROLE_HINT = "tcg-web.role";

export type StoreRole = "owner" | "manager" | "staff";
const ROLE_RANK: Record<StoreRole, number> = { owner: 3, manager: 2, staff: 1 };

/** True when `role` meets or exceeds `min`. Null role (unknown) never passes. */
export function roleAtLeast(role: StoreRole | null, min: StoreRole): boolean {
  return role != null && ROLE_RANK[role] >= ROLE_RANK[min];
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean): void {
  try {
    if (on) window.localStorage.setItem(key, "1");
    else window.localStorage.removeItem(key);
  } catch {
    // localStorage unavailable — flags are best-effort hints only.
  }
}

function readRole(): StoreRole | null {
  try {
    const v = window.localStorage.getItem(ROLE_HINT);
    return v === "owner" || v === "manager" || v === "staff" ? v : null;
  } catch {
    return null;
  }
}

function writeRole(role: StoreRole | null): void {
  try {
    if (role) window.localStorage.setItem(ROLE_HINT, role);
    else window.localStorage.removeItem(ROLE_HINT);
  } catch {
    // best-effort hint only
  }
}

export class SignInCancelledError extends Error {
  constructor() {
    super("Sign-in was cancelled");
    this.name = "SignInCancelledError";
  }
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Dev-only guest bypass; always false in production builds. */
  isGuest: boolean;
  /** Caller's store role for UI gating (null until /auth/me resolves). */
  role: StoreRole | null;
  signInWithEbay: () => Promise<void>;
  signInWithPasskey: () => Promise<void>;
  cancelSignIn: () => void;
  /** Dev-only guest bypass; a no-op in production builds. */
  loginAsGuest: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface MeResponse {
  id: string;
  ebayUsername: string | null;
  role: StoreRole;
  storeId: string;
}

// Cold-start session probe. The session cookie is sent automatically. Returns
// the /auth/me body on success; "invalid" only on an explicit 401, so a
// transient/offline failure keeps a previously-signed-in user logged in.
async function checkSession(): Promise<
  { status: "valid"; me: MeResponse } | { status: "invalid" | "offline" }
> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/me`);
    if (res.ok)
      return { status: "valid", me: (await res.json()) as MeResponse };
    if (res.status === 401) return { status: "invalid" };
    return { status: "offline" };
  } catch {
    return { status: "offline" };
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState<StoreRole | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        // Dev-only guest bypass — eliminated from production builds. Guests act
        // as Owner so the full UI is exercised in dev.
        if (import.meta.env.DEV && readFlag(GUEST_FLAG)) {
          setIsGuest(true);
          setRole("owner");
          setIsAuthenticated(true);
          return;
        }
        const result = await checkSession();
        if (result.status === "valid") {
          writeFlag(SIGNED_IN_HINT, true);
          setRole(result.me.role);
          writeRole(result.me.role);
          setIsAuthenticated(true);
        } else if (result.status === "offline" && readFlag(SIGNED_IN_HINT)) {
          // Offline cold start on a known-signed-in device: stay in with the
          // last-known role so the UI doesn't collapse to the restricted view.
          setRole(readRole());
          setIsAuthenticated(true);
        } else {
          writeFlag(SIGNED_IN_HINT, false);
          writeRole(null);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Marks the session authenticated and pulls the role from /auth/me for UI
  // gating. Called after every successful sign-in / sign-up.
  const storeSession = useCallback(async () => {
    writeFlag(SIGNED_IN_HINT, true);
    setIsAuthenticated(true);
    const result = await checkSession();
    if (result.status === "valid") {
      setRole(result.me.role);
      writeRole(result.me.role);
    }
  }, []);

  // Dev-only auth bypass (localStorage-only, no server session). The body is
  // behind `import.meta.env.DEV`, so production builds compile it to a no-op
  // and the "Continue as guest" button is stripped from the login screen.
  const loginAsGuest = useCallback(async () => {
    if (!import.meta.env.DEV) return;
    writeFlag(GUEST_FLAG, true);
    setIsGuest(true);
    setRole("owner");
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch(`${API_BASE}/api/auth/logout`, { method: "POST" });
    } catch {
      // Best-effort — clear local state regardless of the network result.
    }
    if (import.meta.env.DEV) writeFlag(GUEST_FLAG, false);
    writeFlag(SIGNED_IN_HINT, false);
    writeRole(null);
    setIsGuest(false);
    setRole(null);
    setIsAuthenticated(false);
  }, []);

  const cancelSignIn = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const signInWithEbay = useCallback(async () => {
    cancelledRef.current = false;

    const startRes = await fetch(`${API_BASE}/api/auth/ebay/start`, {
      method: "POST",
    });
    if (!startRes.ok) {
      const body = (await startRes.json().catch(() => ({}))) as {
        error?: string;
      };
      throw new Error(
        body.error ?? `Could not start sign-in (${startRes.status})`,
      );
    }
    const { url, nonce } = (await startRes.json()) as {
      url: string;
      nonce: string;
    };
    if (!url || !nonce) {
      throw new Error("Sign-in start returned an unexpected response.");
    }

    window.open(url, "_blank", "noopener,noreferrer");

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      if (cancelledRef.current) throw new SignInCancelledError();
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (cancelledRef.current) throw new SignInCancelledError();
      try {
        const pollRes = await fetch(
          `${API_BASE}/api/auth/ebay/poll-session?nonce=${encodeURIComponent(nonce)}`,
        );
        if (!pollRes.ok) continue;
        const body = (await pollRes.json()) as { status: string };
        // The poll response itself sets the HttpOnly session cookie (TNK-54);
        // a "ready" status means the cookie is now in this window's jar.
        if (body.status === "ready") {
          await storeSession();
          return;
        }
      } catch {
        // ignore transient network errors and keep polling
      }
    }
    throw new Error("Sign-in timed out. Please try again.");
  }, [storeSession]);

  const signInWithPasskey = useCallback(async () => {
    // The verify step sets the session cookie server-side (TNK-54/55); we just
    // flip local state once the ceremony succeeds.
    await loginWithPasskey();
    await storeSession();
  }, [storeSession]);

  useEffect(() => {
    setOnUnauthorized(() => {
      logout();
    });
    return () => setOnUnauthorized(null);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isGuest,
        isLoading,
        role,
        signInWithEbay,
        signInWithPasskey,
        cancelSignIn,
        loginAsGuest,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Current store role (null until /auth/me resolves). UI gating only. */
export function useRole(): StoreRole | null {
  return useAuth().role;
}

/**
 * Renders `children` only when the caller's role meets `min` (owner > manager >
 * staff); otherwise renders `fallback`. UX affordance only — server guards
 * (TNK-78) remain the source of truth.
 */
export function RequireRole({
  min,
  children,
  fallback = null,
}: {
  min: StoreRole;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}): React.ReactNode {
  const { role } = useAuth();
  return roleAtLeast(role, min) ? children : fallback;
}
